// Purely presentational shell: backdrop + draggable handle + sheet
// surface. All physics live in useBottomSheet — this component
// just wires the hook's ref/handlers/state to markup so it can be
// reused for any future sheet content (not just purchases).
//
// Rendered via a portal into the app-shell's overlay root (see
// OverlayPortalProvider) rather than inline where it's called from.
// Bottom sheets are opened from inside scrollable pages (Market),
// and `position: absolute` on the sheet would otherwise resolve
// against that scrolling ancestor — scroll the page while the sheet
// is open and it drags away with it. Portaling to the non-scrolling
// app root keeps it pinned to the viewport regardless of where it's
// triggered from.

import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '@/utils/cx';
import { CloseIcon } from '@/assets/icons';
import { useOverlayPortalNode } from '@/providers/OverlayPortalProvider';
import type { UseBottomSheetResult } from '@/hooks/useBottomSheet';
import styles from './BottomSheet.module.scss';

export interface BottomSheetProps {
  bottomSheet: UseBottomSheetResult;
  children: ReactNode;
}

export function BottomSheet({ bottomSheet, children }: BottomSheetProps) {
  const { sheetRef, isOpen, isMounted, close, dragHandlers } = bottomSheet;
  const portalNode = useOverlayPortalNode();

  if (!portalNode) return null;

  return createPortal(
    <>
      <div
        className={cx(styles.backdrop, isOpen && styles.show)}
        onClick={() => close(0)}
        aria-hidden="true"
      />
      {isMounted && (
        <div className={styles.sheet} ref={sheetRef} role="dialog" aria-modal="true">
          <div className={styles.handle} {...dragHandlers} />
          <button type="button" className={styles.closeButton} onClick={() => close(0)} aria-label="Close">
            <CloseIcon size={15} />
          </button>
          {children}
        </div>
      )}
    </>,
    portalNode,
  );
}
